import { memo, useCallback, useEffect, useState } from 'react';
import { Modal, Form, Button, Select, Input, Typography } from 'antd';
import { Customer, User } from '../../../../fsm/users';

import styles from './index.module.css';
import { ASSIGNABLE_ROLE_OPTIONS } from '../../utils/roleCapabilities';
import { UserRole } from '../../utils/role';
import CustomizePermissionsDrawer from '../customize-permissions-drawer';

const { Text } = Typography;

interface Props {
  isOpen: boolean;
  isLoading: boolean;
  customers: Customer[];
  user: User | null;
  canSeeAddUserCustomerField: boolean;
  /** Shows the "Customize permissions" action for an existing user, opening
   * CustomizePermissionsDrawer against their real per-user overrides. */
  canCustomizeUserPermissions: boolean;
  onClose: () => void;
  onFetchCustomers: () => void;
  onUpdateUser: (data: Partial<User>, id: number) => void;
  onSubmit: (
    firstName: string,
    lastName: string,
    email: string,
    role: string,
    customerId?: number,
  ) => void;
}

const AddUserModal = ({
  isOpen,
  isLoading,
  customers,
  user,
  canSeeAddUserCustomerField,
  canCustomizeUserPermissions,
  onClose,
  onSubmit,
  onFetchCustomers,
  onUpdateUser,
}: Props) => {
  const [form] = Form.useForm();
  const [isCustomizeDrawerOpen, setIsCustomizeDrawerOpen] = useState(false);
  const selectedRole = Form.useWatch('role', form) as UserRole | undefined;
  const selectedRoleDescription = ASSIGNABLE_ROLE_OPTIONS.find(
    (option) => option.value === selectedRole,
  )?.description;
  // Customizing an existing person's access is the coherent flow -- a
  // brand-new user has no id yet for a per-user override to attach to.
  const canOpenCustomizeDrawer = Boolean(
    user && canCustomizeUserPermissions && selectedRole,
  );

  useEffect(() => {
    if (isOpen && canSeeAddUserCustomerField) {
      onFetchCustomers();
    }
    if (user) {
      form.setFieldsValue({
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        role: user.role,
        customerId: user.customerId,
      });
    }
  }, [isOpen, canSeeAddUserCustomerField]);

  const handleModalClose = useCallback(() => {
    setIsCustomizeDrawerOpen(false);
    onClose();
  }, [onClose]);

  const handleSave = useCallback(async () => {
    try {
      await form.validateFields();
      const firstName = form.getFieldValue('firstName');
      const lastName = form.getFieldValue('lastName');
      const email = form.getFieldValue('email');
      const role = form.getFieldValue('role');
      const customerId = form.getFieldValue('customerId');
      if (user) {
        onUpdateUser({ firstName, lastName, email, role, customerId }, user.id);
      } else {
        onSubmit(firstName, lastName, email, role, customerId);
      }
      handleModalClose();
      form.resetFields();
    } catch (errorInfo) {
      console.error('Validation failed:', errorInfo);
    }
  }, [user, form, onSubmit, handleModalClose, onUpdateUser]);

  return (
    <Modal
      title="Add new user"
      open={isOpen}
      confirmLoading={isLoading}
      onCancel={handleModalClose}
      width={'520px'}
      footer={
        <>
          <Button onClick={handleModalClose}>Cancel</Button>
          <Button type="primary" onClick={handleSave}>
            {user ? 'Update' : 'Add'} user
          </Button>
        </>
      }
    >
      <Form
        form={form}
        layout="vertical"
        style={{ width: '100%' }}
        autoComplete="off"
      >
        <div className={styles.content}>
          <Form.Item
            name="firstName"
            label="First name"
            rules={[{ required: true }]}
          >
            <Input size="large" />
          </Form.Item>
          <Form.Item
            name="lastName"
            label="Last name"
            rules={[{ required: true }]}
          >
            <Input size="large" />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              {
                required: true,
                message: 'Please input your name',
              },
              { type: 'email' },
            ]}
          >
            <Input size="large" />
          </Form.Item>
          <Form.Item
            name="role"
            label="Role"
            rules={[{ required: true }]}
            // Antd renders Form.Item help text below the control by default,
            // which is exactly where the description needs to be -- visible
            // as soon as a role is picked, not just inside the closed dropdown.
            extra={
              selectedRoleDescription && (
                <Text type="secondary" className={styles.roleDescription}>
                  {selectedRoleDescription}
                </Text>
              )
            }
          >
            <Select
              size="large"
              options={ASSIGNABLE_ROLE_OPTIONS}
              // Shows each role's description inside the open dropdown too,
              // so the choice is informed before committing, not only after.
              optionRender={(option) => (
                <div className={styles.roleOption}>
                  <div>{option.data.label}</div>
                  <Text
                    type="secondary"
                    className={styles.roleOptionDescription}
                  >
                    {option.data.description}
                  </Text>
                </div>
              )}
            />
          </Form.Item>
          {canOpenCustomizeDrawer && (
            <Button
              type="link"
              className={styles.customizeButton}
              onClick={() => setIsCustomizeDrawerOpen(true)}
            >
              Customize permissions for this user
            </Button>
          )}
          {canSeeAddUserCustomerField && (
            <Form.Item
              name="customerId"
              label="Customer name"
              rules={[{ required: true }]}
            >
              <Select
                size="large"
                options={customers.map((item) => ({
                  label: item.name,
                  value: item.id,
                }))}
              />
            </Form.Item>
          )}
        </div>
      </Form>
      {canOpenCustomizeDrawer && user && (
        <CustomizePermissionsDrawer
          open={isCustomizeDrawerOpen}
          onClose={() => setIsCustomizeDrawerOpen(false)}
          userId={user.id}
          userName={`${user.firstName ?? ''} ${user.lastName ?? ''}`.trim()}
          role={selectedRole as UserRole}
        />
      )}
    </Modal>
  );
};

export default memo(AddUserModal);
